/**
 * Mission/Objective Utilities
 *
 * Handles both legacy single-string missions and new multi-goal JSON format.
 * Format: [{ rank: 1, goal: "Building Emergency Savings" }, ...]
 */

/**
 * Parse mission data from storage — handles both legacy string and JSON array.
 * @param {string|null|undefined} missionData - Raw value from currentMission or objective_text
 * @returns {Array<{rank: number, goal: string}>} - Normalized array, sorted by rank
 */
export function parseMissions(missionData) {
  if (!missionData || (typeof missionData === 'string' && missionData.trim() === '')) {
    return [];
  }

  // Normalize ranks: sort then re-assign sequential 1,2,3...
  const normalize = (arr) =>
    arr.sort((a, b) => a.rank - b.rank).map((item, i) => ({ ...item, rank: i + 1 }));

  // Decode HTML entities from legacy rows that were stored HTML-escaped
  const decode = (s) => s
    .replace(/&quot;/g, '"')
    .replace(/&#x27;/g, "'")
    .replace(/&#x2F;/g, '/')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&amp;/g, '&');

  // Try JSON parse first
  if (typeof missionData === 'string' && missionData.trim().startsWith('[')) {
    const candidate = missionData.includes('&quot;') ? decode(missionData) : missionData;
    try {
      const parsed = JSON.parse(candidate);
      if (Array.isArray(parsed) && parsed.length > 0 && parsed[0].goal) {
        return normalize(parsed);
      }
    } catch {
      // Not valid JSON — treat as legacy string
    }
  }

  // Already an array (from in-memory state)
  if (Array.isArray(missionData)) {
    return normalize(missionData);
  }

  // Legacy single string
  return [{ rank: 1, goal: String(missionData) }];
}

/**
 * Get the primary (rank 1) mission goal string.
 * @param {string|Array|null} missionData
 * @returns {string|null}
 */
export function getPrimaryMission(missionData) {
  const missions = parseMissions(missionData);
  return missions.length > 0 ? missions[0].goal : null;
}

/**
 * Format missions for display — truncated with "+N more".
 * @param {string|Array|null} missionData
 * @param {number} maxShow - How many goals to show before truncating
 * @returns {string} e.g. "Emergency Savings, Debt +1 more"
 */
export function formatMissionDisplay(missionData, maxShow = 2) {
  const missions = parseMissions(missionData);
  if (missions.length === 0) return '';
  if (missions.length === 1) return missions[0].goal;

  const shown = missions.slice(0, maxShow).map(m => m.goal);
  const remaining = missions.length - maxShow;

  if (remaining > 0) {
    return `${shown.join(', ')} +${remaining} more`;
  }
  return shown.join(', ');
}

/**
 * Check if mission data represents multiple goals.
 * @param {string|Array|null} missionData
 * @returns {boolean}
 */
export function isMultiGoal(missionData) {
  return parseMissions(missionData).length > 1;
}

/**
 * Strip any leading "Stage N" / "Tier N" prefix from a stage title.
 *
 * The app numbers stages itself ("STAGE 1", "STAGE 2", ...), so a title that
 * already embeds a number ("Tier 1: Learn the basics") would read as
 * "STAGE 1 · Tier 1: Learn the basics". This cleans legacy/LLM data at display
 * time so the duplication never shows. Handles separators like ":", "-", ".".
 *
 * @param {string|null|undefined} title
 * @returns {string} cleaned title (original, trimmed, if no prefix present)
 */
export function cleanStageTitle(title) {
  if (!title || typeof title !== 'string') return title || '';
  // Remove a leading "Stage 1", "Tier 2", etc. plus any trailing separator.
  const cleaned = title.replace(/^\s*(?:stage|tier)\s*\d+\s*[:.\-–—)]*\s*/i, '').trim();
  // Guard: if the title was ONLY a prefix (e.g. "Stage 1"), keep the original.
  return cleaned.length > 0 ? cleaned : title.trim();
}
